import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import MasterViewController from "../../redux/actions/masterApiActions";
import { useDispatch, useSelector } from "react-redux";
import Editor from "react-simple-wysiwyg";
import { FaPlus, FaTrash } from "react-icons/fa";
import {
  Grid,
  Typography,
  IconButton,
  Button,
  Box,
  TextField,
  MenuItem,
  Autocomplete,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FormDrawer from "../../components/layout/FormDrawer";
import MobileSelectField from "../../components/mobile/MobileSelectField";
import {
  BRAND,
  primaryButtonSx,
  inputSx,
  mobileInputSx,
} from "../../components/auth/authTheme";

import "../../styles/styles.css";
import "../../styles/layout.css";

const applicabilityOptions = [
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Half-Yearly", value: "half_yearly" },
  { label: "Annually", value: "annually" },
  { label: "On Demand", value: "on_demand" },
];

const challanTypeOptions = [
  { label: "Percentage", value: "percentage" },
  { label: "Fixed Amount", value: "fixed" },
  { label: "Salary Range Based", value: "salary_range" },
  { label: "Custom Logic", value: "custom" },
];

const contributionTypeOptions = [
  { label: "Percentage", value: "percentage" },
  { label: "Fixed", value: "fixed" },
  { label: "Salary Range", value: "salary_range" },
];

const monthOptions = [
  { full: "January", short: "Jan" },
  { full: "February", short: "Feb" },
  { full: "March", short: "Mar" },
  { full: "April", short: "Apr" },
  { full: "May", short: "May" },
  { full: "June", short: "Jun" },
  { full: "July", short: "Jul" },
  { full: "August", short: "Aug" },
  { full: "September", short: "Sep" },
  { full: "October", short: "Oct" },
  { full: "November", short: "Nov" },
  { full: "December", short: "Dec" },
];

const SectionCard = ({ title, isMobile, children, mb = 1.5 }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: isMobile ? "12px" : 0,
      border: isMobile ? `1px solid ${BRAND.border}` : "none",
      p: isMobile ? 1.5 : 0,
      mb: isMobile ? mb : 3,
    }}
  >
    {title && (
      <Typography
        variant="subtitle2"
        sx={{ color: BRAND.navy, fontWeight: 700, mb: 1.5 }}
      >
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

function ChallanFormModal({ show, handleClose, initialData = null }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = isMobile ? mobileInputSx : inputSx;
  const gridSpacing = isMobile ? 1.5 : 2.5;

  const { addChallanMaster, updateChallan } = MasterViewController();
  const isEdit = Boolean(initialData?.id);
  const { isLoding } = useSelector((state) => state.master);

  const [initialValues, setInitialValues] = useState({
    name: "",
    amount: "",
    percentage: "",
    description: "",
    salary_range_amount: {},
    challan_type: "",
    applicability: "monthly",
    applicable_salary_component: "",
    applicable_months: [],
  });

  const [salaryRangeEntries, setSalaryRangeEntries] = useState([["", ""]]);

  useEffect(() => {
    if (show && initialData) {
      setInitialValues({
        name: initialData.name || "",
        amount: initialData.amount || "",
        percentage: initialData.percentage || "",
        description: initialData.description || "",
        salary_range_amount: initialData.salary_range_amount || {},
        challan_type: initialData.challan_type || "",
        applicability: initialData.applicability || "monthly",
        applicable_salary_component: initialData.applicable_salary_component || "",
        applicable_months: initialData.applicable_months || [],
        employee_contribution: initialData.employee_contribution || {},
        employer_contribution: initialData.employer_contribution || {},
      });

      const entries = Object.entries(initialData.salary_range_amount || {});
      setSalaryRangeEntries(entries.length ? entries : [["", ""]]);
    } else if (show && !initialData) {
      setInitialValues({
        name: "",
        amount: "",
        percentage: "",
        description: "",
        salary_range_amount: {},
        challan_type: "",
        applicability: "monthly",
        applicable_salary_component: "",
        applicable_months: [],
        employee_contribution: {},
        employer_contribution: {},
      });
      setSalaryRangeEntries([["", ""]]);
    }
  }, [show, initialData]);

  const handleAddSalaryRange = () => {
    setSalaryRangeEntries([...salaryRangeEntries, ["", ""]]);
  };

  const handleRemoveSalaryRange = (index) => {
    const updated = [...salaryRangeEntries];
    updated.splice(index, 1);
    setSalaryRangeEntries(updated);
  };

  const handleSalaryRangeChange = (index, type, value) => {
    const updated = [...salaryRangeEntries];
    updated[index][type === "key" ? 0 : 1] = value;
    setSalaryRangeEntries(updated);
  };

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    const salary_range_amount = Object.fromEntries(salaryRangeEntries);
    const finalValues = { ...values, salary_range_amount };

    try {
      const action = isEdit
        ? updateChallan(initialData.id, finalValues)
        : addChallanMaster(finalValues);
      await dispatch(action);
      resetForm();
      handleClose();
    } catch (error) {
      console.error("Error submitting challan form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderFooter = (isSaving, submitForm) =>
    isMobile ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disabled={isSaving}
          onClick={() => submitForm()}
          fullWidth
          sx={{
            ...primaryButtonSx,
            minHeight: 48,
            borderRadius: "12px",
            "&.Mui-disabled": {
              bgcolor: isSaving ? BRAND.orange : undefined,
              color: isSaving ? "#fff" : undefined,
              opacity: isSaving ? 0.85 : undefined,
            },
          }}
          startIcon={
            isSaving ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {isSaving
            ? isEdit
              ? "Updating..."
              : "Saving..."
            : isEdit
              ? "Update"
              : "Submit"}
        </Button>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={isSaving}
          fullWidth
          sx={{ minHeight: 44, textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
      </Box>
    ) : (
      <>
        <Button onClick={handleClose} color="inherit" disabled={isSaving}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disabled={isSaving}
          onClick={() => submitForm()}
          sx={{
            ...primaryButtonSx,
            minWidth: 120,
            "&.Mui-disabled": {
              bgcolor: isSaving ? BRAND.orange : undefined,
              color: isSaving ? "#fff" : undefined,
              opacity: isSaving ? 0.85 : undefined,
            },
          }}
          startIcon={
            isSaving ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {isSaving
            ? isEdit
              ? "Updating..."
              : "Saving..."
            : isEdit
              ? "Update"
              : "Submit"}
        </Button>
      </>
    );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnBlur
      validateOnChange={false}
    >
      {({ values, setFieldValue, handleChange, isSubmitting, submitForm }) => {
        const isSaving = isSubmitting || isLoding;

        return (
          <Form style={{ display: "contents" }}>
            <FormDrawer
              open={show}
              onClose={handleClose}
              title={isEdit ? "Edit Challan" : "Add Challan"}
              subtitle="Configure deduction rules and contribution settings"
              width={{ xs: "100%", sm: 640, md: 760 }}
              footer={renderFooter(isSaving, submitForm)}
            >
              <SectionCard title="Challan details" isMobile={isMobile}>
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Challan Name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {isMobile ? (
                      <MobileSelectField
                        label="Challan Type"
                        value={values.challan_type}
                        onChange={(val) => setFieldValue("challan_type", val)}
                        options={challanTypeOptions}
                        placeholder="Select type"
                      />
                    ) : (
                      <Autocomplete
                        fullWidth
                        options={challanTypeOptions}
                        getOptionLabel={(option) => option.label}
                        value={
                          challanTypeOptions.find(
                            (opt) => opt.value === values.challan_type
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          setFieldValue(
                            "challan_type",
                            newValue ? newValue.value : ""
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Challan Type"
                            name="challan_type"
                            sx={fieldSx}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Amount (₹)"
                      name="amount"
                      type="number"
                      value={values.amount}
                      onChange={handleChange}
                      disabled={["percentage", "salary_range"].includes(
                        values.challan_type
                      )}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Percentage (%)"
                      name="percentage"
                      type="number"
                      value={values.percentage}
                      onChange={handleChange}
                      disabled={["fixed", "salary_range"].includes(
                        values.challan_type
                      )}
                      sx={fieldSx}
                    />
                  </Grid>

                  {values.challan_type === "salary_range" && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Salary Range Amounts
                        <IconButton size="small" onClick={handleAddSalaryRange}>
                          <FaPlus color="#0d6efd" />
                        </IconButton>
                      </Typography>

                      {salaryRangeEntries.map(([key, value], index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 1.5,
                            flexDirection: isMobile ? "column" : "row",
                          }}
                        >
                          <TextField
                            fullWidth
                            placeholder="Range (e.g. 0-10000)"
                            value={key}
                            onChange={(e) =>
                              handleSalaryRangeChange(index, "key", e.target.value)
                            }
                            sx={fieldSx}
                          />
                          <TextField
                            fullWidth
                            type="number"
                            placeholder="Amount"
                            value={value}
                            onChange={(e) =>
                              handleSalaryRangeChange(index, "value", e.target.value)
                            }
                            sx={fieldSx}
                          />
                          <IconButton
                            onClick={() => handleRemoveSalaryRange(index)}
                            sx={{ alignSelf: isMobile ? "flex-end" : "center" }}
                          >
                            <FaTrash color="red" />
                          </IconButton>
                        </Box>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </SectionCard>

              <SectionCard title="Employee contribution" isMobile={isMobile}>
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} md={4}>
                    {isMobile ? (
                      <MobileSelectField
                        label="Type"
                        value={values.employee_contribution?.type || ""}
                        onChange={(val) =>
                          setFieldValue("employee_contribution.type", val)
                        }
                        options={contributionTypeOptions}
                        placeholder="Select type"
                      />
                    ) : (
                      <TextField
                        select
                        fullWidth
                        label="Type"
                        name="employee_contribution.type"
                        value={values.employee_contribution?.type || ""}
                        onChange={handleChange}
                        sx={fieldSx}
                      >
                        {contributionTypeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Value"
                      name="employee_contribution.value"
                      type="number"
                      value={values.employee_contribution?.value || ""}
                      onChange={handleChange}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Salary Cap (Optional)"
                      name="employee_contribution.salary_cap"
                      type="number"
                      value={values.employee_contribution?.salary_cap || ""}
                      onChange={handleChange}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard title="Employer contribution" isMobile={isMobile}>
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} md={4}>
                    {isMobile ? (
                      <MobileSelectField
                        label="Type"
                        value={values.employer_contribution?.type || ""}
                        onChange={(val) =>
                          setFieldValue("employer_contribution.type", val)
                        }
                        options={contributionTypeOptions}
                        placeholder="Select type"
                        helperText="For ESI and PF contribution only"
                      />
                    ) : (
                      <TextField
                        select
                        fullWidth
                        label="Type"
                        name="employer_contribution.type"
                        value={values.employer_contribution?.type || ""}
                        onChange={handleChange}
                        helperText="For ESI and PF contribution only"
                        sx={fieldSx}
                      >
                        {contributionTypeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Value"
                      name="employer_contribution.value"
                      type="number"
                      value={values.employer_contribution?.value || ""}
                      onChange={handleChange}
                      helperText="For ESI/PF — include PF Pension Scheme % if applicable"
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Salary Cap (Optional)"
                      name="employer_contribution.salary_cap"
                      type="number"
                      value={values.employer_contribution?.salary_cap || ""}
                      onChange={handleChange}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard title="Applicability & settings" isMobile={isMobile}>
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} md={6}>
                    {isMobile ? (
                      <MobileSelectField
                        label="Applicability"
                        value={values.applicability}
                        onChange={(val) => setFieldValue("applicability", val)}
                        options={applicabilityOptions}
                        placeholder="Select applicability"
                      />
                    ) : (
                      <Autocomplete
                        fullWidth
                        options={applicabilityOptions}
                        getOptionLabel={(option) => option.label}
                        value={
                          applicabilityOptions.find(
                            (opt) => opt.value === values.applicability
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          setFieldValue(
                            "applicability",
                            newValue ? newValue.value : ""
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Applicability"
                            name="applicability"
                            sx={fieldSx}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Applicable Salary Component"
                      name="applicable_salary_component"
                      value={values.applicable_salary_component}
                      onChange={handleChange}
                      placeholder="e.g. basic_salary or gross_salary"
                      helperText="e.g. Basic Salary or Gross Salary"
                      sx={fieldSx}
                    />
                  </Grid>

                  {["quarterly", "half_yearly", "annually"].includes(
                    values.applicability
                  ) && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontWeight: 600, color: BRAND.navy }}
                      >
                        Select Applicable Months
                      </Typography>
                      <Box
                        sx={
                          isMobile
                            ? {
                                display: "grid",
                                gridTemplateColumns: "repeat(4, 1fr)",
                                gap: 0.75,
                              }
                            : {
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                              }
                        }
                      >
                        {monthOptions.map(({ full, short }) => {
                          const isSelected = values.applicable_months.includes(full);
                          return (
                            <Box
                              key={full}
                              onClick={() => {
                                const updated = isSelected
                                  ? values.applicable_months.filter((m) => m !== full)
                                  : [...values.applicable_months, full];
                                setFieldValue("applicable_months", updated);
                              }}
                              sx={{
                                px: isMobile ? 0.5 : 2,
                                py: isMobile ? 0.85 : 1,
                                border: `1px solid ${isSelected ? BRAND.orange : BRAND.border}`,
                                borderRadius: "8px",
                                backgroundColor: isSelected ? BRAND.orange : BRAND.bg,
                                color: isSelected ? "#fff" : BRAND.text,
                                cursor: "pointer",
                                fontWeight: 500,
                                fontSize: isMobile ? "0.75rem" : "0.875rem",
                                textAlign: "center",
                                minHeight: isMobile ? 36 : undefined,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {isMobile ? short : full}
                            </Box>
                          );
                        })}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </SectionCard>

              <SectionCard title="Description" isMobile={isMobile} mb={0}>
                <Box
                  sx={{
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: "10px",
                    "& .rsw-editor": {
                      minHeight: isMobile ? 160 : 200,
                      maxHeight: isMobile ? 240 : 300,
                      overflowY: "auto",
                    },
                  }}
                >
                  <Editor
                    value={values.description}
                    onChange={(e) => setFieldValue("description", e.target.value)}
                  />
                </Box>
              </SectionCard>
            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
}

export default ChallanFormModal;
